import React, { ReactNode } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { usePromotion } from '../../services/Promotion.hooks';
import validationSchema from './Form.validation';
import { v4 as uuid } from 'uuid';
import { usePromotions } from '../../../../services/Promotions.hooks';
import { useToast } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import {
  EDisplay,
  TAddBannerParams,
  TUploadMediaData,
} from '@/api/promoManager/banners/banner.types';
import {
  useMutationAddPromotion,
  useMutationUploadMedia,
} from '@/api/promoManager/promotions/promotions.hooks';
import { TData, EStatus } from '@/api/promoManager/promotions/promotions.types';

type TFormikHOC = {
  children: ReactNode;
};

export type TFormikValues = TData;

export default function FormHOC({ children }: TFormikHOC) {
  const { promotionId } = useParams();
  const { promotionToShow, isLoading } = usePromotion({ id: promotionId });
  const { data: bannerList } = usePromotions();
  const { mutateAsync } = useMutationAddPromotion();
  const { mutateAsync: mutateMedia } = useMutationUploadMedia();
  const toast = useToast();
  const intl = useIntl();
  const navigate = useNavigate();

  if (isLoading) return <></>;

  const initialValues: TData = {
    id: promotionToShow?.id ?? uuid(),
    title: promotionToShow?.title ?? '',
    media: {
      desktop: {
        name: `${uuid()}-dekstop`,
        base64: promotionToShow?.media?.desktop?.publicUrl,
        publicUrl: promotionToShow?.media.desktop?.publicUrl,
      },
      mobile: {
        name: `${uuid()}-mobile`,
        base64: promotionToShow?.media?.mobile?.publicUrl,
        publicUrl: promotionToShow?.media?.mobile?.publicUrl,
      },
      app: {
        name: `${uuid()}-app`,
        base64: promotionToShow?.media?.app?.publicUrl,
        publicUrl: promotionToShow?.media.app?.publicUrl,
      },
    },
    location: promotionToShow?.location ?? [],
    punter_category: promotionToShow?.punter_category ?? [],
    display: promotionToShow?.display ?? EDisplay.Both,
    status: promotionToShow?.status ?? EStatus.Draft,
    button_link: promotionToShow?.button_link ?? '',
    button_text: promotionToShow?.button_text ?? '',
    description: promotionToShow?.description ?? '',
    terms_and_conditions: promotionToShow?.terms_and_conditions ?? '',
    start_date: promotionToShow?.start_date ?? '',
    end_date: promotionToShow?.end_date ?? '',
    has_money_back_promo: promotionToShow?.has_money_back_promo ?? false,
    money_back_promo_id: promotionToShow?.money_back_promo_id ?? '',
    has_treasure_hunt_promo: promotionToShow?.has_treasure_hunt_promo ?? false,
    treasure_hunt_promo_id: promotionToShow?.treasure_hunt_promo_id ?? '',
    available_on: promotionToShow?.available_on ?? [],
  };

  return (
    <Formik<TFormikValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={async (val, { setSubmitting }) => {
        const desktopName = `${uuid()}-desktop`;
        const mobileName = `${uuid()}-mobile`;
        const appName = `${uuid()}-app`;

        async function urlToBase64(url: string): Promise<string> {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = (reader.result as string).split(',')[1];
              resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }

        const desktopBase64 = await urlToBase64(
          promotionToShow?.media?.desktop?.publicUrl ?? ''
        );

        const mobileBase64 = await urlToBase64(
          promotionToShow?.media?.mobile?.publicUrl ?? ''
        );

        const appBase64 = await urlToBase64(
          promotionToShow?.media?.app?.publicUrl ?? ''
        );

        const getMediaObject = (
          name: string,
          contentBase64: string | undefined
        ) => ({
          name,
          content: promotionToShow ? uuid() : undefined,
          contentBase64,
          contentType: 'image/png',
        });

        const mediaObjects = [
          getMediaObject(
            desktopName,
            val?.media?.desktop?.publicUrl?.split(',')[1] ?? desktopBase64
          ),
          getMediaObject(
            mobileName,
            val?.media?.mobile?.publicUrl?.split(',')[1] ?? mobileBase64
          ),
          getMediaObject(
            appName,
            val?.media?.app?.publicUrl?.split(',')[1] ?? appBase64
          ),
        ];

        if (val?.media?.app?.base64) {
          mediaObjects.push(
            getMediaObject(
              `${uuid()}-app`,
              val?.media?.app?.base64?.split(',')[1]
            )
          );
        }

        mutateMedia(mediaObjects as TAddBannerParams[]).then(
          (data: TUploadMediaData) => {
            const desktop = data.find((e) => e?.name === desktopName);
            const mobile = data.find((e) => e?.name === mobileName);
            const app = data.find((e) => e?.name === appName);

            const links: TFormikValues = {
              ...val,
              media: {
                desktop: {
                  publicUrl: desktop?.publicUrl,
                  name: `${uuid()}-desktop`,
                  link: val.media.desktop?.link,
                  content: uuid(),
                },
                mobile: {
                  publicUrl: mobile?.publicUrl,
                  content: uuid(),
                  name: `${uuid()}-mobile`,
                },
                app: {
                  publicUrl: app?.publicUrl,
                  link: val.media.app?.link,
                  name: `${uuid()}-app`,
                },
              },
            };

            const dataArray = [links]; // Convert data object into an array
            const mergedArray = dataArray.concat(
              bannerList?.filter((l) => l.id !== val.id) ?? []
            ); // Merge the arrays
            const base64String = btoa(JSON.stringify(mergedArray));

            mutateAsync({
              contentBase64: base64String,
              contentType: 'application/json',
              version: Number(uuid()),
            }).then(() => {
              toast({
                title: intl.formatMessage({
                  id: 'promomanagerpage.banner.toast.success',
                }),
                status: 'success',
                position: 'top-right',
                duration: 5000,
              });

              navigate(`/promo-manager/promotions`);
            });
          }
        );

        setSubmitting(true);
      }}
    >
      <Form>{children}</Form>
    </Formik>
  );
}
